import { VesData } from '../VesData';
import { RecordingError } from '../Errors';
import { RemainingRecordedVideoData } from './RemainingRecordedVideoData';
import { RecordedVideoUploader } from './RecordedVideoUploader';
import { RecordingStrategyBase } from './RecordingStrategyBase';
import { getRecentUploadSpeed, getSizeInMB } from '@/functions';
import { EventNames, RecordingStrategyNames } from '@/constants';

const { CONNECTED, ERROR, PROGRESS_UPDATE, UPLOAD_SPEED_UPDATE, UPLOAD_SUCCESS } =
  EventNames.RecordedVideoUploader;

export class DurableUploadStrategy extends RecordingStrategyBase {
  remainingVideoData = new RemainingRecordedVideoData();

  uploader: RecordedVideoUploader | null = null;

  constructor(
    public videoId: string,
    public containerFormat: string,
    public beginConnectingTime: number,
    private vesData: VesData,
    private authToken: string,
  ) {
    super(videoId, containerFormat, beginConnectingTime);
    this.vesData = vesData;
    this.authToken = authToken;
  }

  // eslint-disable-next-line class-methods-use-this
  get strategyName() {
    return RecordingStrategyNames.HTTP;
  }

  setMediaCodecsRecorded(audioCodecRecorded: string, videoCodecRecorded: string) {
    this.uploader?.handleReadyForEagerCompletion(audioCodecRecorded, videoCodecRecorded);
  }

  createUploader() {
    try {
      this.uploader = new RecordedVideoUploader(
        this.remainingVideoData,
        this.vesData,
        this.videoId,
        this.containerFormat,
        this.authToken,
      );

      this.uploader.on(CONNECTED, this.emitConnected.bind(this));
      this.uploader.on(PROGRESS_UPDATE, this.emitProgressUpdate.bind(this));
      this.uploader.on(UPLOAD_SPEED_UPDATE, this.emitUploadSpeedUpdate.bind(this));
      this.uploader.on(UPLOAD_SUCCESS, (videoUrl: string) => {
        if (!this.uploader) {
          return;
        }
        this.totalVideoSizeInMB = getSizeInMB(this.uploader.videoSizeTotal);
        this.totalUploadSpeedInMbps = getRecentUploadSpeed(this.uploader.allUploadSpeedDetails);
        this.emitUploadSuccess(videoUrl);
      });
      this.uploader.on(ERROR, this.emitError.bind(this));

      // Errors from create promise get emitted by the uploader and handled by emitError
      this.uploader.create();
    } catch (error) {
      this.emitUnknownError(error);
    }
  }

  handleNewDataEvent(event: BlobEvent) {
    try {
      if (event.data && event.data.size > 0) {
        if (this.firstProgressTime === null) {
          this.firstProgressTime = performance.mark('start-data');
        }
        this.remainingVideoData.addVideoData(event.data);
      }
    } catch (error) {
      this.emitUnknownError(error);
    }
  }

  finishUpload() {
    try {
      if (!this.uploader) {
        throw new Error('Uploader is no longer available in DurableUploadStrategy');
      }
      // Errors from finish promise get emitted by the uploader and handled by emitError
      this.uploader.finish();
    } catch (error) {
      this.emitUnknownError(error);
    }
  }

  dispose() {
    this.remainingVideoData.dispose();
    this.uploader = null;
  }

  /**
   * @param {ConstructorParameters<typeof RecordingError>[2]} error
   */
  emitUnknownError(error: unknown) {
    const message = 'An unknown error occurred while recording the video.';
    const metricType = 'http_unknown';
    const recordingError = new RecordingError(message, metricType, error);
    this.emitError(recordingError);
  }
}
